import { FC, FormEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Loader } from 'components';
import { TextField, ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import { useAuth } from 'contexts';
import { useCustomerAPI } from 'hooks/use-customer-api';
import useTimeZone from 'hooks/use-timezone';
import { Customer, Role } from 'types';
import { parseEmailList } from 'utils/format';

import EmailSelect from '../email-select';
import HourSelect from '../report-select';

const NOTES_MAX_LENGTH = 250;

export const EditCustomer: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { userProfile } = useAuth();

  const { getCustomerById, updateCustomer, validateCode } = useCustomerAPI();
  const { timeZoneIndicator } = useTimeZone();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [originalCustomer, setOriginalCustomer] = useState<Customer | null>(null);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!!params.id && !isNaN(Number(params.id))) {
      getCustomerById(Number(params.id)).then(customer => {
        setCustomer(customer);
        setOriginalCustomer(customer);
      });
    }
  }, [getCustomerById, params.id]);

  const handleReset: FormEventHandler<HTMLFormElement> = useCallback(() => {
    if (!!params.id) {
      navigate(`/settings/customers/${params.id}`, { replace: true });
      return;
    }
    navigate('/settings/customers', { replace: true });
  }, [navigate, params.id]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      setIsSubmitting(true);

      if (!customer || !customer.name || !customer.code) {
        setIsSubmitting(false);
        return;
      }

      if ((customer.notes?.length ?? 0) > NOTES_MAX_LENGTH) {
        setIsSubmitting(false);
        return;
      }

      const response = await updateCustomer({
        id: customer.id,
        name: customer.name,
        code: customer.code,
        email: customer.email,
        location: customer.location,
        deleted: customer.deleted,
        ediRecipients: customer.ediRecipients ?? [],
        activityReportHours: customer.activityReportHours ?? [],
        statusReportHours: customer.statusReportHours ?? [],
        inventoryReportHours: customer.inventoryReportHours ?? [],
        interchangeRecipients: customer.interchangeRecipients ?? [],
        sendAutomatedReport: customer.sendAutomatedReport ?? false,
        notes: customer.notes
      });

      setIsSubmitting(false);
      if (response) navigate(`/settings/customers/${response.id}`, { replace: true });
    },
    [customer, navigate, updateCustomer]
  );

  const validateCodeInput = useCallback(
    async (code: string): Promise<ValidationResult> => {
      if (customer?.code === originalCustomer?.code) {
        return { status: ValidationStatus.SUCCESS };
      }

      if (!code || !customer?.location.id) {
        return { status: ValidationStatus.DEFAULT };
      }

      const isValid = await validateCode(code, customer.location.id);
      if (isValid) {
        return { status: ValidationStatus.SUCCESS };
      }

      return { status: ValidationStatus.WARNING, error: 'Code not available.' };
    },
    [customer, originalCustomer, validateCode]
  );

  return (
    <Loader loading={!customer}>
      {!!customer && !!originalCustomer && (
        <form
          className="page"
          id="edit-customer"
          onReset={handleReset}
          onSubmit={handleSubmit}
          ref={formRef}
          data-cy="edit-customer-form"
        >
          <div className="page-header">
            <h1>
              CUSTOMER {originalCustomer.name} ({originalCustomer.code})
            </h1>
            <div className="d-flex g-3 flex-row-reverse">
              <Loader loading={isSubmitting}>
                <button className="btn-primary" type="submit">
                  SUBMIT
                </button>
              </Loader>
              <button className="btn-outline" type="reset">
                CANCEL
              </button>
            </div>
          </div>
          <div className="page-content flex-fill">
            {[Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role) && (
              <div className="card-primary">
                <div className="card-header px-3">
                  <h4 className="text-uppercase">Customer Information</h4>
                </div>
                <div className="card-body">
                  <div className="grid-4 p-2">
                    <div>
                      <h6 className="text-info-dark">Name</h6>
                      <input
                        type="text"
                        className="mt-2 w-100"
                        defaultValue={customer.name ?? ''}
                        onChange={e => setCustomer(c => c && { ...c, name: e.target.value })}
                        required
                        data-cy="edit-customer-name-input"
                      />
                    </div>
                    <div>
                      <h6 className="text-info-dark">Code</h6>
                      <TextField
                        className="mt-2"
                        value={customer.code ?? ''}
                        onChange={code => setCustomer(c => c && { ...c, code })}
                        validate={validateCodeInput}
                        data-cy="edit-customer-code-input"
                      />
                    </div>
                    <div>
                      <h6 className="text-info-dark">Location</h6>
                      <p className="text-black mt-2">{customer.location.name}</p>
                    </div>
                  </div>
                  <div className="grid-2 p-2">
                    <div>
                      <h6 className="text-info-dark">Notes</h6>
                      <textarea
                        className="mt-2 w-100"
                        rows={3}
                        maxLength={NOTES_MAX_LENGTH}
                        value={customer.notes ?? ''}
                        onChange={e => setCustomer(c => c && { ...c, notes: e.target.value })}
                        data-cy="edit-customer-notes-input"
                      />
                      <small
                        className={(customer.notes?.length ?? 0) >= NOTES_MAX_LENGTH ? 'text-danger' : 'text-info-dark'}
                      >
                        {customer.notes?.length ?? 0}/{NOTES_MAX_LENGTH}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="card-primary">
              <div className="card-header px-3">
                <h4 className="text-uppercase">Email</h4>
              </div>
              <div className="card-body">
                <div className="p-2 d-flex align-items-center g-2">
                  <h6 className="text-info-dark mb-0">Send Automated Reports</h6>
                  <label className="switch mb-0">
                    <input
                      type="checkbox"
                      checked={customer.sendAutomatedReport === true}
                      onChange={e => setCustomer(c => c && { ...c, sendAutomatedReport: e.target.checked })}
                      data-cy="edit-customer-send-automated-reports-input"
                    />
                    <span className="slider round" />
                  </label>
                </div>
                <div className="p-2">
                  <h6 className="text-info-dark">Daily Report Email Addresses</h6>
                  <EmailSelect email={customer.email} setEmail={email => setCustomer(c => c && { ...c, email })} />
                </div>
                <div className="p-2">
                  <h6 className="text-info-dark">Activity Report ({timeZoneIndicator})</h6>
                  <HourSelect
                    id="activity-report-hours"
                    hours={customer.activityReportHours}
                    setHours={activityReportHours => setCustomer(c => c && { ...c, activityReportHours })}
                  />
                </div>
                <div className="p-2">
                  <h6 className="text-info-dark">Status Report ({timeZoneIndicator})</h6>
                  <HourSelect
                    id="activity-report-hours"
                    hours={customer.statusReportHours}
                    setHours={statusReportHours => setCustomer(c => c && { ...c, statusReportHours })}
                  />
                </div>
                <div className="p-2">
                  <h6 className="text-info-dark">Inventory Report ({timeZoneIndicator})</h6>
                  <HourSelect
                    id="inventory-report-hours"
                    hours={customer.inventoryReportHours}
                    setHours={inventoryReportHours => setCustomer(c => c && { ...c, inventoryReportHours })}
                  />
                </div>
                <div className="p-2">
                  <h6 className="text-info-dark">Interchange Receipt Email Addresses</h6>
                  <EmailSelect
                    email={customer.interchangeRecipients?.join(',')}
                    setEmail={value => setCustomer(c => c && { ...c, interchangeRecipients: parseEmailList(value) })}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </Loader>
  );
};
